import { channelForMessageType, isSignallingType } from "./contract.ts";
import type { ChannelMessageMap, DataChannelName, ParseResult, SessionMessage, SignallingEnvelope } from "./types.ts";
import { validateMessageType } from "./validate.ts";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readType(value: unknown): ParseResult<string> {
  if (!isRecord(value) || typeof value.type !== "string" || value.type.length === 0) {
    return { ok: false, errors: ["message type is missing"] };
  }
  return { ok: true, value: value.type };
}

export function parseSignallingEnvelope(value: unknown): ParseResult<SignallingEnvelope> {
  const type = readType(value);
  if (!type.ok) return type;
  if (!isSignallingType(type.value)) {
    return { ok: false, errors: [`${type.value} is not a signalling message`] };
  }
  const validated = validateMessageType(type.value, value);
  if (!validated.ok) return validated;
  return { ok: true, value: value as SignallingEnvelope };
}

export function parseChannelMessage<C extends DataChannelName>(
  channel: C,
  value: unknown,
): ParseResult<ChannelMessageMap[C]> {
  const type = readType(value);
  if (!type.ok) return type;
  const owner = channelForMessageType(type.value);
  if (owner !== channel) {
    return { ok: false, errors: [`${type.value} is not valid on ${channel}`] };
  }
  const validated = validateMessageType(type.value, value);
  if (!validated.ok) return validated;
  return { ok: true, value: value as ChannelMessageMap[C] };
}

export function parseSessionMessage(value: unknown): ParseResult<SessionMessage> {
  const type = readType(value);
  if (!type.ok) return type;
  const validated = validateMessageType(type.value, value);
  if (!validated.ok) return validated;
  return { ok: true, value: value as SessionMessage };
}

const REDACTED: Record<string, readonly string[]> = {
  "clipboard.set": ["payloadBase64"],
  "file.chunk": ["payloadBase64"],
};

export function redactForLog(value: unknown): unknown {
  if (!isRecord(value) || typeof value.type !== "string") return value;
  const fields = REDACTED[value.type];
  if (!fields) return value;
  const copy: Record<string, unknown> = { ...value };
  for (const field of fields) {
    if (field in copy) copy[field] = "[redacted]";
  }
  return copy;
}
